﻿<template>
  <div class="knowledge-overview-page">
    <div class="page-header">
      <h1>AI知识库管理</h1>
      <p>管理和维护多个医疗知识库</p>
    </div>

    <!-- 操作栏 -->
    <div class="action-bar">
      <div class="search-section">
        <el-input
          v-model="searchQuery"
          placeholder="搜索知识库..."
          prefix-icon="Search"
          clearable
          style="width: 300px"
        />
        <el-select v-model="statusFilter" placeholder="选择状态" clearable style="width: 150px">
          <el-option label="全部" value="" />
          <el-option label="活跃" value="active" />
          <el-option label="维护中" value="maintenance" />
          <el-option label="已停用" value="inactive" />
        </el-select>
      </div>
      <el-button type="primary" @click="createKnowledgeBase">
        <el-icon><Plus /></el-icon>
        新建知识库
      </el-button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="6" animated />
    </div>

    <!-- 总体统计 -->
    <!-- <div v-else class="overview-stats">
      <div class="stat-item">
        <div class="stat-number">{{ totalKnowledgeBases }}</div>
        <div class="stat-label">知识库总数</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">{{ totalEntries }}</div>
        <div class="stat-label">总条目数</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">{{ activeKnowledgeBases }}</div>
        <div class="stat-label">活跃知识库</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">{{ todayUpdates }}</div>
        <div class="stat-label">今日更新</div>
      </div>
    </div> -->

    <!-- 知识库列表 -->
    <div v-if="!loading" class="knowledge-bases-grid">
      <div 
        v-for="kb in filteredKnowledgeBases" 
        :key="kb.id"
        class="knowledge-base-card"
        @click="enterKnowledgeBase(kb)"
      >
        <div class="card-header">
          <div class="kb-title">{{ kb.name }}</div>
          <div class="kb-status">
            <el-tag :type="getStatusType(kb.status)" size="small">
              {{ getStatusText(kb.status) }}
            </el-tag>
          </div>
        </div>
        
        <div class="card-content">
          <p class="kb-description">{{ kb.description }}</p>
          
          <div class="kb-stats">
            <div class="stat-row">
              <span class="stat-label">条目数:</span>
              <span class="stat-value">{{ kb.entryCount.toLocaleString() }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">分类数:</span>
              <span class="stat-value">{{ kb.categoryCount }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">最后更新:</span>
              <span class="stat-value">{{ kb.lastUpdate }}</span>
            </div>
          </div>
        </div>
        
        <div class="card-footer">
          <div class="kb-categories">
            <el-tag 
              v-for="category in kb.categories" 
              :key="category"
              size="small"
              class="category-tag"
            >
              {{ category }}
            </el-tag>
          </div>
          <div class="card-actions">
            <el-button 
              disabled
              type="text" 
              size="small"
              @click.stop="editKnowledgeBase(kb)"
            >
              编辑
            </el-button>
            <el-button 
              disabled
              type="text" 
              size="small"
              @click.stop="deleteKnowledgeBase(kb)"
              style="color: #f56c6c;"
            >
              删除
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新建/编辑知识库对话框 -->
    <el-dialog
      v-model="knowledgeBaseDialogVisible"
      :title="isEdit ? '编辑知识库' : '新建知识库'"
      width="50%"
    >
      <el-form :model="knowledgeBaseForm" label-width="100px">
        <el-form-item label="知识库名称">
          <el-input v-model="knowledgeBaseForm.name" placeholder="请输入知识库名称" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input
            v-model="knowledgeBaseForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入知识库描述"
          />
        </el-form-item>
        <el-form-item label="分类">
          <el-select
            v-model="knowledgeBaseForm.categories"
            multiple
            placeholder="选择分类"
            style="width: 100%"
          >
            <el-option label="肾病知识" value="肾病知识" />
            <el-option label="治疗方案" value="治疗方案" />
            <el-option label="用药指南" value="用药指南" />
            <el-option label="饮食建议" value="饮食建议" />
            <el-option label="护理知识" value="护理知识" />
            <el-option label="康复指导" value="康复指导" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="knowledgeBaseForm.status">
            <el-radio label="active">活跃</el-radio>
            <el-radio label="maintenance">维护中</el-radio>
            <el-radio label="inactive">已停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="knowledgeBaseDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveKnowledgeBase">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'
import { agentAPI } from '@/api'

const router = useRouter()

const searchQuery = ref('')
const statusFilter = ref('')
const knowledgeBaseDialogVisible = ref(false)
const isEdit = ref(false)
const loading = ref(false)

const knowledgeBaseForm = reactive({
  name: '',
  description: '',
  categories: [],
  status: 'active'
})

// 将 knowledgeBases 改为 ref，用于存储从接口获取的数据
const knowledgeBases = ref([])

// 获取知识库列表
const fetchKnowledgeBases = async () => {
  try {
    loading.value = true
    const response = await agentAPI.getDatasetsList({})
    
    if (response && response.code === 0) {
      // 解析嵌套的 JSON 字符串
      let dataArray = []
      if (typeof response.data === 'string') {
        const parsedData = JSON.parse(response.data)
        dataArray = parsedData.data || []
      } else if (response.data && response.data.data) {
        dataArray = response.data.data
      } else if (Array.isArray(response.data)) {
        dataArray = response.data
      }
      
      // 将接口返回的数据转换为组件需要的格式
      knowledgeBases.value = dataArray.map((item, index) => ({
        id: item.id || `kb-${index + 1}`,
        name: item.name || '未命名知识库',
        description: item.description || '暂无描述',
        status: getStatusFromPermission(item.permission),
        entryCount: item.document_count || 0,
        categoryCount: item.tags ? item.tags.length : 0,
        lastUpdate: formatTimestamp(item.updated_at),
        categories: item.tags || []
      }))
    } else {
      ElMessage.error('获取知识库列表失败')
      // 如果接口失败，使用默认数据
      knowledgeBases.value = getDefaultKnowledgeBases()
    }
  } catch (error) {
    console.error('获取知识库列表失败:', error)
    ElMessage.error('获取知识库列表失败')
    // 如果接口失败，使用默认数据
    knowledgeBases.value = getDefaultKnowledgeBases()
  } finally {
    loading.value = false
  }
}

// 默认数据（作为备用）
const getDefaultKnowledgeBases = () => [
  {
    id: 1,
    name: '慢性肾病知识库',
    description: '包含慢性肾病的诊断、治疗、护理等全方位知识内容',
    status: 'active',
    entryCount: 2846,
    categoryCount: 4,
    lastUpdate: '2024-01-15',
    categories: ['肾病知识', '治疗方案', '用药指南', '饮食建议']
  },
  {
    id: 2,
    name: '透析治疗知识库',
    description: '透析治疗相关的专业知识和技术指导',
    status: 'active',
    entryCount: 1523,
    categoryCount: 3,
    lastUpdate: '2024-01-14',
    categories: ['治疗方案', '护理知识', '康复指导']
  }
]

// 计算属性
const totalKnowledgeBases = computed(() => knowledgeBases.value.length)
const totalEntries = computed(() => knowledgeBases.value.reduce((sum, kb) => sum + kb.entryCount, 0))
const activeKnowledgeBases = computed(() => knowledgeBases.value.filter(kb => kb.status === 'active').length)
const todayUpdates = computed(() => {
  const today = new Date().toISOString().split('T')[0]
  return knowledgeBases.value.filter(kb => kb.lastUpdate === today).length
})

const filteredKnowledgeBases = computed(() => {
  let filtered = knowledgeBases.value
  
  if (searchQuery.value) {
    filtered = filtered.filter(kb => 
      kb.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      kb.description.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  }
  
  if (statusFilter.value) {
    filtered = filtered.filter(kb => kb.status === statusFilter.value)
  }
  
  return filtered
})

const getStatusType = (status) => {
  const types = {
    'active': 'success',
    'maintenance': 'warning',
    'inactive': 'info'
  }
  return types[status] || 'default'
}

const getStatusText = (status) => {
  const texts = {
    'active': '活跃',
    'maintenance': '维护中',
    'inactive': '已停用'
  }
  return texts[status] || status
}

const createKnowledgeBase = () => {
  isEdit.value = false
  Object.assign(knowledgeBaseForm, {
    name: '',
    description: '',
    categories: [],
    status: 'active'
  })
  knowledgeBaseDialogVisible.value = true
}

const editKnowledgeBase = (kb) => {
  isEdit.value = true
  Object.assign(knowledgeBaseForm, {
    name: kb.name,
    description: kb.description,
    categories: [...kb.categories],
    status: kb.status
  })
  knowledgeBaseDialogVisible.value = true
}

const deleteKnowledgeBase = (kb) => {
  ElMessageBox.confirm(
    `确定要删除知识库"${kb.name}"吗？`,
    '警告',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }
  ).then(() => {
    const index = knowledgeBases.value.findIndex(item => item.id === kb.id)
    if (index > -1) {
      knowledgeBases.value.splice(index, 1)
    }
    ElMessage.success('删除成功')
  })
}

const saveKnowledgeBase = () => {
  if (!knowledgeBaseForm.name || !knowledgeBaseForm.description) {
    ElMessage.error('请填写完整信息')
    return
  }
  
  if (isEdit.value) {
    ElMessage.success('编辑成功')
  } else {
    const newId = Math.max(...knowledgeBases.value.map(kb => kb.id)) + 1
    knowledgeBases.value.push({
      id: newId,
      name: knowledgeBaseForm.name,
      description: knowledgeBaseForm.description,
      status: knowledgeBaseForm.status,
      entryCount: 0,
      categoryCount: knowledgeBaseForm.categories.length,
      lastUpdate: new Date().toISOString().split('T')[0],
      categories: [...knowledgeBaseForm.categories]
    })
    ElMessage.success('新建成功')
  }
  knowledgeBaseDialogVisible.value = false
}

const enterKnowledgeBase = (kb) => {
  // 跳转到知识库详情页面
  router.push({
    name: 'KnowledgeBaseDetail',
    params: { id: kb.id },
    query: { name: kb.name }
  })
}

// 组件挂载时获取数据
onMounted(() => {
  fetchKnowledgeBases()
})

// 根据权限转换为状态
const getStatusFromPermission = (permission) => {
  switch (permission) {
    case 'all_team_members':
      return 'active'
    case 'only_me':
      return 'maintenance'
    default:
      return 'active'
  }
}

// 格式化时间戳
const formatTimestamp = (timestamp) => {
  if (!timestamp) return new Date().toISOString().split('T')[0]
  const date = new Date(timestamp * 1000)
  return date.toISOString().split('T')[0]
}
</script>

<style scoped>
.knowledge-overview-page {
  padding: 20px;
}

.page-header {
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 28px;
  color: #1e293b;
  margin: 0 0 10px 0;
}

.page-header p {
  color: #64748b;
  margin: 0;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-section {
  display: flex;
  gap: 16px;
}

.loading-container {
  margin-bottom: 30px;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-item {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 8px;
}

.stat-label {
  color: #64748b;
  font-size: 14px;
}

.knowledge-bases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.knowledge-base-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.knowledge-base-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border-color: #3b82f6;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.kb-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  margin-right: 12px;
}

.kb-status {
  flex-shrink: 0;
}

.card-content {
  margin-bottom: 16px;
}

.kb-description {
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

.kb-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.stat-label {
  color: #64748b;
}

.stat-value {
  color: #1e293b;
  font-weight: 500;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.kb-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  margin-right: 12px;
}

.category-tag {
  font-size: 12px;
}

.card-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .action-bar {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .search-section {
    flex-direction: column;
  }
  
  .knowledge-bases-grid {
    grid-template-columns: 1fr;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .kb-categories {
    margin-right: 0;
    justify-content: center;
  }
}
</style>
